import {useMutation} from '@tanstack/react-query'
import {useAuthUser} from 'react-auth-kit'

import moment from 'moment'
import {useIntl} from 'react-intl'
import {useNavigate, useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {API} from 'src/feature/bite/domain'
import {getTimeZone} from 'src/feature/bite/helpers/date'
import {Error, Success} from 'src/feature/bite/helpers/notification'
import useRequestParams from 'src/feature/bite/hook/request'
import {ItemListSchemaResponse} from '../domain'
import {useItem} from '../hook'

export function useUpdateItem() {
  const auth = useAuthUser()
  const {itemId} = useParams()
  const {headers, errors} = useRequestParams()
  const navigate = useNavigate()

  return useMutation(
    ['ItemUpdate', itemId],
    async (values: any) => {
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Item/UpsertItem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth()?.token,
        },
        body: JSON.stringify({
          ...values,
          id: itemId,
          userID: auth()?.id,
          language: headers.Language,
          timeZoneOffset: timeZoneOffset,
          availableFrom: moment(values.availableFrom, 'DD-MM-YYYY HH:mm:ss').format(
            'YYYY-MM-DDTHH:mm:ss'
          ),
          availableUntil: moment(values.availableUntil, 'DD-MM-YYYY HH:mm:ss').format(
            'YYYY-MM-DDTHH:mm:ss'
          ),
        }),
      })

      const Data = await Response.json()

      if (!Response.ok || !Data.responseResult.result) {
        Error(Data?.responseResult?.exceptionDetail || errors.packageItem.update_error)

        return
      }

      Success(errors.packageItem.update_success)
      navigate('/items')

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.packageItem.update_error)
      },
    }
  )
}

export function useCreateItem() {
  const {headers, errors} = useRequestParams()
  const auth = useAuthUser()
  const router = useNavigate()

  const userID = auth()?.id

  return useMutation(
    ['ItemCreate'],
    async (values: any) => {
      const timeZoneOffset = getTimeZone()

      values.taxRate = !values.taxRate ? 5 : values.taxRate
      values.price = !values.price ? 0 : values.price

      const Response = await fetch(`${API}/Item/UpsertItem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth()?.token,
        },
        body: JSON.stringify({
          ...values,
          // storeID: '996225251718',
          userID,
          language: headers.Language,
          timeZoneOffset: timeZoneOffset,
          availableFrom: moment(values.availableFrom, 'DD-MM-YYYY HH:mm:ss').format(
            'YYYY-MM-DDTHH:mm:ss'
          ),
          availableUntil: moment(values.availableUntil, 'DD-MM-YYYY HH:mm:ss').format(
            'YYYY-MM-DDTHH:mm:ss'
          ),
        }),
      })

      const Data = await Response.json()

      if (!Response.ok || !Data.responseResult.result) {
        Error(Data?.responseResult?.exceptionDetail || errors.packageItem.create_error)

        return
      }

      Success(errors.packageItem.create_success)

      setTimeout(() => {
        router('/items')
      }, 2000)

      return true
    },
    {
      onSuccess: () => {
        //window.location.href = '/items'
      },
      onError: (error) => {
        Error(errors.product.create_error)
      },
    }
  )
}

export function useUploadItem() {
  const auth = useAuthUser()
  const {headers, signOut} = useRequestParams()
  const {listSet, listFilter} = useItem()
  const intl = useIntl()

  return useMutation(
    ['UploadFile'],
    async (file: File) => {
      const timeZoneOffset = getTimeZone()

      const userID = auth()?.id
      const formData = new FormData()
      formData.append('userID', userID)
      formData.append('file', file)
      formData.append('language', headers.Language)
      formData.append('timeZoneOffset', timeZoneOffset.toString())

      const ResponseUpload = await fetch(`${API}/Item/UploadItemFile`, {
        method: 'POST',
        headers: {Authorization: headers.Authorization},
        body: formData,
      })

      const DataUpload = await ResponseUpload.json()

      if (!ResponseUpload.ok || !DataUpload.responseResult.result) {
        Error(DataUpload?.responseResult?.exceptionDetail || 'Error')
        return
      }

      const Response = await fetch(`${API}/Item/GetItemList`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          userID,
          language: headers.Language,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const safeParse = ItemListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        Error(intl.formatMessage({id: 'ERROR'}))
      }

      const parse = ItemListSchemaResponse.parse(Data)

      listSet(parse.response)

      Success('File uploaded successfully')

      return Data
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        // Error(errors.referralProgram.upload_error)
      },
    }
  )
}

export function useAddCategory() {
  const intl = useIntl()
  const {auth, headers, errors} = useRequestParams()
  const {categories, categoriesSet} = useItem()

  const userID = auth()?.id

  return useMutation(
    ['CreateCategory'],
    async ({name}: {name: any}) => {
      if (name.length < 3) {
        Error(intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_LENGTH_FIELD'}, {length: 3}))
        return null
      }

      if (name.length > 50) {
        Error(intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50}))
        return null
      }

      const Response = await fetch(`${API}/Category/AddCategory`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name,
          // storeID: '996225251718',
          userID,
          language: headers.Language,
        }),
      })

      const Data = await Response.json()

      if (!Data || !Data.response.id) {
        Error(Data?.responseResult?.exceptionDetail || errors.category.create_error)
        return null
      }

      Success(errors.category.create_success)

      categoriesSet([
        ...categories,
        {
          id: Data.response.id,
          name: name,
          status: Data.response.status,
        },
      ])

      return {
        id: Data.response.id,
        name: name,
      }
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.category.create_error)
      },
    }
  )
}

export function useAddFamily() {
  const intl = useIntl()
  const {auth, headers, errors} = useRequestParams()
  const {family, familySet} = useItem()

  const userID = auth()?.id

  return useMutation(
    ['CreateCategory'],
    async ({name}: {name: any}) => {
      if (name.length < 3) {
        Error(intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_LENGTH_FIELD'}, {length: 3}))
        return null
      }

      if (name.length > 50) {
        Error(intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50}))
        return null
      }

      const Response = await fetch(`${API}/StoreProductFamily/Create`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name,
          // storeID: '996225251718',
          userID,
          language: headers.Language,
        }),
      })

      const Data = await Response.json()

      if (!Data || !Data.response.id) {
        Error(Data?.responseResult?.exceptionDetail || errors.category.create_error)
        return null
      }

      Success(errors.category.create_success)

      familySet([
        ...family,
        {
          id: Data.response.id,
          name: name,
        },
      ])

      return {
        id: Data.response.id,
        name: name,
      }
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.category.create_error)
      },
    }
  )
}

export function useGetAll() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {list} = useItem()

  const userID = auth()?.id

  const listFilter = {
    filterList: [],
    orderList: [{type: 2, key: 'CreatedOn'}],
    paging: {
      currentPageNumber: 1,
      pageSize: list.paging.totalCount,
    },
  }

  return useMutation(
    ['itemsAll'],
    async () => {
      const Response = await fetch(`${API}/StoreProduct/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          // storeID: '996225251718',
          userID,
          language: headers.Language,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()
      const safeParse = ItemListSchemaResponse.safeParse(Data)
      const parse = ItemListSchemaResponse.parse(Data)

      if (parse.responseResult.result === false) {
        toast.error(parse.responseResult.exceptionDetail || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return []
      }

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      return parse.response.dataList
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useCopyPackageItem() {
  const {headers, errors, signOut} = useRequestParams()
  const {listSet, listFilter} = useItem()
  const auth = useAuthUser()
  const intl = useIntl()
  const userID = auth()?.id

  return useMutation(
    ['CopyPackageItem'],
    async (id: any) => {
      const timeZoneOffset = getTimeZone()

      const response = await fetch(`${API}/Item/CopyItem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth()?.token}`,
        },
        body: JSON.stringify({
          id,
          userID,
          language: headers.Language,
          timeZoneOffset: timeZoneOffset,
        }),
      })

      const data = await response.json()

      if (!response.ok || !data.responseResult.result) {
        toast.error(data?.responseResult?.exceptionDetail || errors.product.create_error)
        return false
      }

      const ResponseList = await fetch(`${API}/Item/GetItemList`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          userID,
          language: headers.Language,
        }),
      })

      if (ResponseList.status === 401) {
        signOut()
      }

      const DataList = await ResponseList.json()

      const parseList = ItemListSchemaResponse.parse(DataList)

      listSet(parseList.response)

      return data
    },
    {
      onSuccess: () => {
        Success(intl.formatMessage({id: 'COPY.PACKAGE_ITEM.SUCCESS'}))
      },
      onError: (error: Error) => {
        // Handle error (e.g., show an error message)
        //console.error('Error upserting membership:', error)
      },
    }
  )
}

export function useDeletePackageItem(refetch) {
  const {headers, errors} = useRequestParams()
  const auth = useAuthUser()
  const intl = useIntl()

  const userID = auth()?.id

  return useMutation(
    ['DeletePackageItem'],
    async (id: any) => {
      const response = await fetch(`${API}/Item/DeleteItem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth()?.token}`,
        },
        body: JSON.stringify({
          id,
          userID,
          language: headers.Language,
        }),
      })

      const data = await response.json()

      if (!response.ok || !data.responseResult.result) {
        toast.error(data?.responseResult?.exceptionDetail || errors.product.create_error)
        return false
      }

      toast.success('Product deleted successfully')
      refetch()

      return data
    },
    {
      onError: (error: Error) => {
        // Handle error (e.g., show an error message)
        console.error('Error when deleting membership:', error)
      },
    }
  )
}

export function useSendPaymentLink() {
  const {headers, errors} = useRequestParams()
  const auth = useAuthUser()
  const userID = auth()?.id
  const intl = useIntl()

  return useMutation(
    ['SendPaymentLink'],
    async (values: any) => {
      const timeZoneOffset = getTimeZone()

      const response = await fetch(`${API}/Item/SendPaymentLink`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth()?.token}`,
        },
        body: JSON.stringify({
          ...values,
          userID,
          language: headers.Language,
          timeZoneOffset: timeZoneOffset,
          expiryDate: moment(values.expiryDate, 'DD-MM-YYYY HH:mm:ss').format(
            'YYYY-MM-DDTHH:mm:ss'
          ),
        }),
      })

      const data = await response.json()

      if (!response.ok || !data.responseResult.result) {
        toast.error(intl.formatMessage({id: 'ERROR'}))
        return false
      }
      /* 
      if (data.response.isSmsSuccess) {
        toast.success(intl.formatMessage({id: 'SEND_PAYMENT_LINK.SMS_SUCCESS'}))
      }

      if (data.response.isEmailSuccess) {
        toast.success(intl.formatMessage({id: 'SEND_PAYMENT_LINK.EMAIL_SUCCESS'}))
      }

      if (data.response.url) {
        toast.success(intl.formatMessage({id: 'SEND_PAYMENT_LINK.URL_SUCCESS'}))
      } */

      return data
    },
    {
      onSuccess: () => {
        // Handle success (e.g., show a success message)
      },
      onError: (error: Error) => {
        // Handle error (e.g., show an error message)
      },
    }
  )
}
